import { GridPoint, TableLayout, ZoneLayout } from '../../../../models/table';

/** A placed table's bounding box in grid cells. */
export interface CellRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export const MIN_CELL_PX = 16;
export const MAX_CELL_PX = 48;
export const MIN_GRID = 4;
export const MAX_GRID = 200;

/**
 * Pixel size of one cell so the whole grid width fits the container, bounded
 * so very small grids do not become enormous and very large ones stay
 * clickable (the canvas then scrolls horizontally).
 */
export function cellPxFor(containerWidth: number, columns: number): number {
  if (!containerWidth || !columns) {
    return MIN_CELL_PX;
  }
  return Math.max(MIN_CELL_PX, Math.min(MAX_CELL_PX, Math.floor(containerWidth / columns)));
}

/** Nearest cell index for a pixel offset. */
export function snapToGrid(px: number, cellPx: number): number {
  return Math.round(px / cellPx);
}

/**
 * Keeps a rect at least 1x1 and entirely inside the grid. Size is clamped
 * before position so an oversized table shrinks to fit rather than being
 * pushed off the far edge.
 */
export function clampRect(rect: CellRect, grid: ZoneLayout): CellRect {
  const width = Math.min(Math.max(1, Math.round(rect.width)), grid.columns);
  const height = Math.min(Math.max(1, Math.round(rect.height)), grid.rows);
  const x = Math.min(Math.max(0, Math.round(rect.x)), grid.columns - width);
  const y = Math.min(Math.max(0, Math.round(rect.y)), grid.rows - height);
  return { x, y, width, height };
}

export function normalizeRotation(degrees: number): number {
  const rounded = Math.round(degrees);
  return ((rounded % 360) + 360) % 360;
}

/**
 * Footprint in cells for a table that has no explicit width/height. Mirrors
 * the seat buckets the waiter app uses so both surfaces agree on the default.
 */
export function defaultTableSize(shape: string, seats: number): { width: number; height: number } {
  const n = Math.max(1, seats || 1);
  const side = Math.min(8, 2 + Math.floor(n / 2));
  switch ((shape || '').toLowerCase()) {
    case 'rectangle':
    case 'oval':
      return {
        width: Math.min(10, 3 + Math.floor(n / 2)),
        height: Math.min(6, 2 + Math.floor(n / 4)),
      };
    case 'circle':
    case 'square':
    default:
      return { width: side, height: side };
  }
}

/** Bounding box for a table's layout, filling in the default size. */
export function layoutRect(layout: TableLayout, shape: string, seats: number): CellRect {
  const fallback = defaultTableSize(shape, seats);
  return {
    x: layout.x,
    y: layout.y,
    width: layout.width ?? fallback.width,
    height: layout.height ?? fallback.height,
  };
}

export function rectsOverlap(a: CellRect, b: CellRect): boolean {
  return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}

/**
 * First top-left cell (row-major scan) where a rect of `size` fits without
 * overlapping any occupied rect. Null when the zone is full.
 */
export function findFreeCell(
  size: { width: number; height: number },
  grid: ZoneLayout,
  occupied: CellRect[],
): { x: number; y: number } | null {
  const width = Math.min(size.width, grid.columns);
  const height = Math.min(size.height, grid.rows);
  for (let y = 0; y + height <= grid.rows; y++) {
    for (let x = 0; x + width <= grid.columns; x++) {
      const candidate = { x, y, width, height };
      if (!occupied.some((rect) => rectsOverlap(candidate, rect))) {
        return { x, y };
      }
    }
  }
  return null;
}

/** Smallest grid that still contains every occupied rect. */
export function requiredGrid(occupied: CellRect[]): ZoneLayout {
  return occupied.reduce(
    (acc, rect) => ({
      columns: Math.max(acc.columns, rect.x + rect.width),
      rows: Math.max(acc.rows, rect.y + rect.height),
    }),
    { columns: 0, rows: 0 },
  );
}

// ---------------------------------------------------------------- outline

export const MIN_OUTLINE_POINTS = 3;
export const MAX_OUTLINE_POINTS = 64;

/** Nearest grid corner for a pixel offset, kept inside 0..columns / 0..rows. */
export function snapPointToCorner(
  px: { x: number; y: number },
  cellPx: number,
  grid: ZoneLayout,
): GridPoint {
  return {
    x: Math.min(grid.columns, Math.max(0, Math.round(px.x / cellPx))),
    y: Math.min(grid.rows, Math.max(0, Math.round(px.y / cellPx))),
  };
}

export function polygonWithinGrid(points: GridPoint[], grid: ZoneLayout): boolean {
  return points.every((p) => p.x >= 0 && p.y >= 0 && p.x <= grid.columns && p.y <= grid.rows);
}

/** Mirrors the backend rules: enough distinct points, no consecutive repeats. */
export function outlineProblem(points: GridPoint[]): string | null {
  if (points.length < MIN_OUTLINE_POINTS) {
    return `Add at least ${MIN_OUTLINE_POINTS} points.`;
  }
  if (points.length > MAX_OUTLINE_POINTS) {
    return `Use at most ${MAX_OUTLINE_POINTS} points.`;
  }
  for (let i = 0; i < points.length; i++) {
    const next = points[(i + 1) % points.length];
    if (points[i].x === next.x && points[i].y === next.y) {
      return 'Two consecutive points are the same.';
    }
  }
  const distinct = new Set(points.map((p) => `${p.x},${p.y}`));
  if (distinct.size < MIN_OUTLINE_POINTS) {
    return `Add at least ${MIN_OUTLINE_POINTS} distinct points.`;
  }
  return null;
}

/**
 * Ray-casting point-in-polygon. Points exactly on an edge count as inside,
 * so a table flush against a wall is not flagged.
 */
export function pointInPolygon(pt: { x: number; y: number }, polygon: GridPoint[]): boolean {
  if (polygon.length < 3) {
    return true;
  }
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    if (onSegment(pt, a, b)) {
      return true;
    }
    const crosses = a.y > pt.y !== b.y > pt.y;
    if (crosses) {
      const xAtY = ((b.x - a.x) * (pt.y - a.y)) / (b.y - a.y) + a.x;
      if (pt.x < xAtY) {
        inside = !inside;
      }
    }
  }
  return inside;
}

function onSegment(p: { x: number; y: number }, a: GridPoint, b: GridPoint): boolean {
  const cross = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
  if (Math.abs(cross) > 1e-9) {
    return false;
  }
  return (
    p.x >= Math.min(a.x, b.x) - 1e-9 &&
    p.x <= Math.max(a.x, b.x) + 1e-9 &&
    p.y >= Math.min(a.y, b.y) - 1e-9 &&
    p.y <= Math.max(a.y, b.y) + 1e-9
  );
}

/**
 * True when the rect's four corners and centre are all inside the polygon.
 * Good enough for flagging tables that poke through a wall; a concave notch
 * smaller than a table could slip through, which is acceptable for a warning.
 */
export function rectInsidePolygon(
  rect: CellRect,
  polygon: GridPoint[] | null | undefined,
): boolean {
  if (!polygon || polygon.length < 3) {
    return true;
  }
  const samples = [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x, y: rect.y + rect.height },
    { x: rect.x + rect.width, y: rect.y + rect.height },
    { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 },
  ];
  return samples.every((s) => pointInPolygon(s, polygon));
}

/** SVG `points` attribute for a polygon at the current cell size. */
export function polygonPoints(points: GridPoint[], cellPx: number): string {
  return points.map((p) => `${p.x * cellPx},${p.y * cellPx}`).join(' ');
}
